import { DestroyRef, Inject, Injectable } from '@angular/core';
import { EMPTY, fromEvent, merge, Observable, of, Subscription } from 'rxjs';
import { catchError, debounceTime, filter, map, switchMap } from 'rxjs/operators';
import { CoreUIToastV2Service } from '../services/toast.service';
import { SESSIONS_PORT } from '../../app/ports/iam/sessions.token';
import { SessionsPort } from '../../app/ports/iam/sessions.port';
import { SessionCtl } from '../services/session.ctl.service';

/**
 * Centralized session-refresh orchestrator.
 *
 * Owns two orthogonal responsibilities:
 *  1. `refreshIfLoggedIn()` — runs a refresh once. Used by APP_INITIALIZER on
 *     bootstrap (covers the F5 case).
 *  2. `init()` — wires `window:focus` and `document:visibilitychange` so the
 *     same refresh runs whenever the user returns to the tab. Debounced 500ms.
 *
 * Session-invalidation detection. We treat the session as invalid in two cases:
 *  - validateToken returns 401: the JWT was revoked (e.g. an admin changed
 *    this user's email — IAM resets the password and invalidates the token).
 *  - validateToken succeeds but returns a different email than the one cached:
 *    defensive coverage in case the backend ever stops invalidating tokens.
 *
 * On invalidation: show an info toast and call logout(). The existing
 * `AuthRedirectEffect` then handles redirecting to /login.
 *
 * Other errors (network down, 500, etc.) are swallowed so a transient backend
 * blip never kicks the user out of an otherwise-valid session.
 */
@Injectable({ providedIn: 'root' })
export class SessionRefreshOnFocusEffect {
  private subscription?: Subscription;

  constructor(
    @Inject(SESSIONS_PORT) private readonly sessions: SessionsPort,
    private readonly sessionCtl: SessionCtl,
    private readonly toast: CoreUIToastV2Service,
    destroyRef: DestroyRef,
  ) {
    destroyRef.onDestroy(() => this.destroy());
  }

  refreshIfLoggedIn(): Observable<void> {
    if (!this.sessionCtl.isLoggedIn) return EMPTY;

    const previousEmail = this.sessionCtl.currentUser?.email?.trim().toLowerCase();

    return this.sessions.refreshCurrentSession().pipe(
      switchMap((updated) => {
        const newEmail = updated?.user?.email?.trim().toLowerCase();
        const emailChangedExternally = !!previousEmail && !!newEmail && newEmail !== previousEmail;

        if (emailChangedExternally) {
          return this.handleSessionInvalidated();
        }
        return of(void 0);
      }),
      catchError((err: { status?: number } | null | undefined) => {
        if (err?.status === 401) {
          return this.handleSessionInvalidated();
        }
        return of(void 0);
      }),
      map(() => void 0),
    );
  }

  init(): void {
    if (this.subscription) return;

    this.subscription = merge(
      fromEvent(window, 'focus'),
      fromEvent(document, 'visibilitychange').pipe(
        filter(() => document.visibilityState === 'visible'),
      ),
    )
      .pipe(
        debounceTime(500),
        switchMap(() => this.refreshIfLoggedIn()),
      )
      .subscribe();
  }

  destroy(): void {
    this.subscription?.unsubscribe();
    this.subscription = undefined;
  }

  private handleSessionInvalidated(): Observable<void> {
    // Defer the toast emission so it survives the bootstrap-time race:
    // on F5 with an invalid token, this runs INSIDE provideAppInitializer
    // before AppComponent has rendered the toast container, so a synchronous
    // emission would be sent to no subscribers and silently lost. setTimeout
    // pushes the emission past the current bootstrap microtask, by which
    // time the toast component has subscribed.
    setTimeout(() => {
      this.toast.show({
        data: {
          type: 'info',
          title: 'Tu sesión expiró',
          msg: 'Puede que tus credenciales hayan cambiado. Si es así, revisa tu correo para obtener las nuevas y vuelve a iniciar sesion.',
          autoClose: true,
          closable: true,
          autoCloseTime: 4500,
        },
      });
    }, 300);

    return this.sessions.logout().pipe(
      catchError(() => of(void 0)),
      map(() => void 0),
    );
  }
}
